import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import { Page, LayoutSingleColumn, NamedLink, ListingCard } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './LandingPage.module.css';

const RENT_PANEL = {
  title: 'Go on, take a peek.',
  text: 'See where it goes.',
  linkText: 'See how renting & buying works',
};
const LEND_PANEL = {
  title: 'Give your closet’s exes',
  text: 'A second chance at love.',
  linkText: 'See how lending & selling works',
};

const SearchBar = () => {
  const [keywords, setKeywords] = useState('');
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const handleSubmit = e => {
    e.preventDefault();
    const searchParams = keywords ? { keywords } : {};
    const to = createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams);
    history.push(to);
  };

  return (
    <div className={css.searchStage}>
      <div className={css.searchInner}>
        <form className={css.searchBar} onSubmit={handleSubmit}>
          <div className={css.searchInputRow}>
            <svg
              className={css.searchIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
            <input
              type="text"
              className={css.searchInput}
              placeholder="Search dresses, denim, date-night everything…"
              aria-label="Search listings"
              value={keywords}
              onChange={e => setKeywords(e.target.value)}
            />
          </div>
          <button type="submit" className={css.searchButton}>
            Browse the closet
          </button>
        </form>
        <p className={css.searchHint}>
          Looking to make some room instead?{' '}
          <a href="#lend-cta" className={css.searchHintLink}>
            Start lending &amp; selling
          </a>
          .
        </p>
      </div>
    </div>
  );
};

const ModeToggle = props => {
  const { mode, onChangeMode } = props;
  const isRent = mode === 'rent';
  const panel = isRent ? RENT_PANEL : LEND_PANEL;

  return (
    <div className={css.toggleStage}>
      <div>
        <div
          className={css.tagToggle}
          role="group"
          aria-label="Choose how you want to use Nothing to Wear"
        >
          <div
            className={classNames(css.thumb, { [css.thumbLend]: !isRent })}
            aria-hidden="true"
          />
          <button
            type="button"
            className={css.toggleButton}
            aria-pressed={isRent}
            onClick={() => onChangeMode('rent')}
          >
            Rent &amp; buy
          </button>
          <button
            type="button"
            className={css.toggleButton}
            aria-pressed={!isRent}
            onClick={() => onChangeMode('lend')}
          >
            Lend &amp; sell
          </button>
        </div>
        <div className={css.pathPanel}>
          <h3 className={css.pathPanelTitle}>{panel.title}</h3>
          <p className={css.pathPanelText}>{panel.text}</p>
          <NamedLink name="HowItWorksPage" className={css.pathPanelLink}>
            {panel.linkText}
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

const TRUST_ITEMS = [
  {
    label: 'Sellers & lenders ID-verified',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6l7-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    label: 'Payments secured by Stripe',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
        <path d="M2.5 9.5h19" />
      </svg>
    ),
  },
  {
    label: 'Based in BC, Canada',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 21s7-6.1 7-11.5A7 7 0 0 0 5 9.5C5 14.9 12 21 12 21z" />
        <circle cx="12" cy="9.5" r="2.3" />
      </svg>
    ),
  },
  {
    label: 'Real humans if something goes sideways',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.4 8.5 8.5 0 0 1-4-1L3 20l1.1-4.5a8.4 8.4 0 0 1-1-4A8.38 8.38 0 0 1 11.6 3a8.5 8.5 0 0 1 9.4 8.5z" />
      </svg>
    ),
  },
];

export const LandingPageComponent = props => {
  const { scrollingDisabled, newestListings } = props;
  const [mode, setMode] = useState('rent');

  return (
    <Page
      title="Nothing to Wear | Rent, Buy, Lend & Sell Fashion in Canada"
      description="Rent, buy, lend, or sell pre-loved clothing with real people near you — Canada's closet-sharing marketplace for sustainable, affordable fashion."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.page} data-mode={mode}>
          <section className={css.hero}>
            <div className={css.heroBg} aria-hidden="true">
              <div className={css.tile} />
              <div className={css.tile} />
              <div className={css.tile} />
              <div className={css.tile} />
              <div className={css.tile} />
              <div className={css.tile} />
            </div>
            <div className={css.heroOverlay} aria-hidden="true" />
            <div className={css.heroContent}>
              <span className={css.eyebrow}>Rent · Buy · Lend · Sell</span>
              <h1 className={css.heroTitle}>
                Well, hello there.
                <br />
                <i className={css.heroTitleItalic}>Nothing to wear?</i>
              </h1>
              <p className={css.subhead}>
                Rent something new for the weekend, buy what someone else fell for first, or let
                your own pieces flirt with somebody new.
              </p>

              <SearchBar />
              <ModeToggle mode={mode} onChangeMode={setMode} />
            </div>
          </section>

          <section className={css.featured}>
            <div className={css.sectionHead}>
              <h2 className={css.sectionHeading}>Fresh off the hanger</h2>
              <p className={css.sectionSubheading}>
                Real pieces, real closets, updated constantly.
              </p>
            </div>
            {newestListings.length > 0 ? (
              <div className={css.listingGrid}>
                {newestListings.map(listing => (
                  <ListingCard
                    key={listing.id.uuid}
                    listing={listing}
                    renderSizes="(max-width: 767px) 100vw, (max-width: 1024px) 50vw, 280px"
                  />
                ))}
              </div>
            ) : (
              <p className={css.placeholderNote}>
                No listings yet — check back soon, or be the first to add one.
              </p>
            )}
          </section>

          <section className={css.trust}>
            {TRUST_ITEMS.map(item => (
              <div className={css.trustItem} key={item.label}>
                <span className={css.trustIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </section>

          <section className={css.cta} id="lend-cta">
            <h2 className={css.ctaHeading}>
              Let&rsquo;s make this <i className={css.ctaHeadingItalic}>official.</i>
            </h2>
            <p className={css.ctaText}>
              Rent, buy, lend, or sell &mdash; commitment-free, unless you&rsquo;re into that.
            </p>
            <div className={css.ctaButtons}>
              <NamedLink name="SearchPage" className={classNames(css.btn, css.btnSolid)}>
                Browse the closet
              </NamedLink>
              <NamedLink name="NewListingPage" className={classNames(css.btn, css.btnGhost)}>
                List your first piece
              </NamedLink>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { newestListingIds } = state.LandingPage;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    newestListings: getListingsById(state, newestListingIds),
  };
};

const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
